import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { take } from 'rxjs/operators';

import { CreateTagError, UpdateTagError } from '../../errors';
import { Tag, TagColor, TagType, User } from '../../models';

import { AuthenticationService } from '../authentication.service';
import { HttpRequestService } from '../http';
import { TagsService } from '../tags.service';
import { UsersService } from '../users.service';

import { FirestoreTimestamp } from './firestore-authentication.service';
import { FirestoreService } from './firestore.service';

export interface TagDTO {
    color: string;
    createdAt: FirestoreTimestamp;
    createdBy: string;
    disabled?: boolean;
    icon?: string;
    id: string;
    name: string;
    priority?: number;
    systemOnly?: boolean;
    translationKey?: string;
    type: string;
}

@Injectable({
    providedIn: 'root',
})
export class FirestoreTagsService implements TagsService {
    private _tagsSubject: Subject<Tag[]>;

    private _tags: Tag[];

    constructor(
        private authenticationService: AuthenticationService,
        private firestoreService: FirestoreService,
        private httpService: HttpRequestService,
        private translateService: TranslateService,
        private usersService: UsersService
    ) {
        this._tagsSubject = new ReplaySubject<Tag[]>(1);

        this.initFirestoreCollectionObservable();
    }

    tags(): Observable<Tag[]> {
        return this._tagsSubject.asObservable();
    }

    async tag(id: string): Promise<Tag> {
        const findTag = (tags: Tag[]) => tags.find((t) => t.id === id);

        return new Promise<Tag>((resolve) => {
            if (this._tags) {
                // tags are already synced with firestore
                resolve(findTag(this._tags));
            } else {
                // tags are not yet resolver, wait until the tagsSubject has any data to resolve it
                this._tagsSubject.pipe(take(1)).subscribe((tags) => resolve(findTag(tags)));
            }
        });
    }

    async createTag(name: string, color: TagColor): Promise<Tag> {
        const tenantId = this.authenticationService.user.tenantId;
        const createTag = this.firestoreService.cloudFunction('Tickets_CreateTag');
        const result = await createTag({ name, color, tenantId });

        if (result && result.data && result.data.code) {
            switch (result.data.code) {
                case 'permission-denied':
                    throw new CreateTagError('PERMISSION_DENIED', "The authenticated user don't have permissions to perform this action.");
                case 'invalid-argument':
                    throw new CreateTagError('MISSING_ARGUMENT', "The property 'name', 'color' or 'tenantId' is missing.");
                case 'already-exists':
                    throw new CreateTagError('NAME_ALREADY_EXISTS', "There are already another 'Tag' with this name.");
                default:
                    throw new CreateTagError('UNKNOWN', 'An unknown error occurred.');
            }
        }

        return result.data;
    }

    async updateTag(tagId: string, data: { name: string; color: TagColor; disabled?: boolean }): Promise<void> {
        const tenantId = this.authenticationService.user.tenantId;
        const updateTag = this.firestoreService.cloudFunction('Tickets_UpdateTag');
        const result = await updateTag({ tagId, tenantId, ...data });

        if (result && result.data) {
            switch (result.data.code) {
                case 'permission-denied':
                    throw new UpdateTagError('PERMISSION_DENIED', "The authenticated user don't have permissions to perform this action.");
                case 'invalid-argument':
                    throw new UpdateTagError('MISSING_ARGUMENT', "The property 'name', 'color' or 'tenantId' is missing.");
                case 'already-exists':
                    throw new UpdateTagError('NAME_ALREADY_EXISTS', "There are already another 'Tag' with this name.");
                default:
                    throw new UpdateTagError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    async changeTagPriorities(tags: Tag[]): Promise<void> {
        const tenantId = this.authenticationService.user.tenantId;
        const changeTag = this.firestoreService.cloudFunction('Tickets_ChangeTagPriority');
        await changeTag({ tenantId, tags });
    }

    private initFirestoreCollectionObservable(): void {
        combineLatest([
            this.firestoreService.tenantDependentCollection<TagDTO>(this.authenticationService.user.tenantId, 'tags').snapshotChanges(),
            this.usersService.users(),
        ]).subscribe((result) => {
            const tagDTOs = result[0].map((td) => {
                const tag = td.payload.doc.data();
                tag.id = td.payload.doc.id;
                return tag;
            });

            const tags = tagDTOs.map((tagDTO) => this.tagMapper(tagDTO, result[1]));

            this._tags = tags;
            this.updateTags(tags);
        });
    }

    private instanceTagType(tagType: string): boolean {
        return ['default', 'channel', 'language', 'direction'].indexOf(tagType) >= 0;
    }

    private tagMapper(tagDTO: TagDTO, users: User[]): Tag {
        return {
            color: tagDTO.color as TagColor,
            createdAt: tagDTO.createdAt && tagDTO.createdAt.toDate(),
            createdBy: users.find((user) => user.id === tagDTO.createdBy),
            disabled: tagDTO.disabled,
            icon: tagDTO.icon,
            id: tagDTO.id,
            name: !tagDTO.createdBy && tagDTO.translationKey ? this.translateService.instant(tagDTO.translationKey) : tagDTO.name,
            priority: tagDTO.priority,
            systemOnly: !!tagDTO.systemOnly,
            type: tagDTO.type && this.instanceTagType(tagDTO.type) ? (tagDTO.type as TagType) : 'default',
        };
    }

    private updateTags(tags: Tag[]): void {
        this._tagsSubject.next(tags);
    }

    async assignOperatorTagsToTicket(ticketId: string, tags: Tag[]): Promise<void> {
        await this.httpService.post<any>('/api/tags', { tenantId: this.authenticationService.user.tenantId, ticketId, tags });
    }
}
